import angular from 'angular';
import _ from 'lodash-es';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr';
import Utils from '~/scripts/services/utils';
import kformUtils from '~/components/kform/utils/kform-utils';
import templateEnableTime from './kdatepicker-enable-time.html';

const INFINITEM = 'INFINITEM';
const INFINITEP = 'INFINITEP';

/**
 * @param {Event} event
 * @returns {*} target
 */
function getEventTarget(event) {
	try {
		if (typeof event.composedPath === 'function') {
			const path = event.composedPath();
			return path[0];
		}
		return event.target;
	}
	catch {
		return event.target;
	}
}

// eslint-disable-next-line no-unused-vars
const enableTimePlugin = (
	/** @type {any} */ pluginConfig,
	/** @type {import('angular').ICompileService} */ $compile,
	/** @type {any} */ $scope,
) => {
	const config = { ...pluginConfig };
	/** @type {HTMLElement} */
	let confirmContainer;

	const confirmButtonCSSClass = 'flatpickr-confirm';

	return (/** @type {any} */ fp) => {
		$scope.options.enableTime = fp.config.enableTime;

		if (fp.config.noCalendar || fp.isMobile) {
			return {};
		}

		return {
			/**
			 * @param {any} __
			 * @param {any} ___
			 * @param {any} ____
			 * @param {KeyboardEvent} event
			 */
			onKeyDown(__, ___, ____, event) {
				const eventTarget = getEventTarget(event);
				if (fp.config.enableTime
					&& event.key === 'Tab'
					&& eventTarget === fp.amPM
				) {
					event.preventDefault();
					confirmContainer.focus();
				}
				else if (event.key === 'Enter' && eventTarget === confirmContainer) {
					fp.close();
				}
			},
			onReady() {
				// eslint-disable-next-line no-underscore-dangle
				confirmContainer = fp._createElement(
					'div',
					'datepicker-check-time',
					templateEnableTime,
				);
				confirmContainer.tabIndex = -1;

				const templateCheckTimeElement = $compile(templateEnableTime)($scope);
				// confirmContainer.innerHTML += config.confirmIcon;
				// confirmContainer.addEventListener('click', fp.close);
				angular.element(fp.calendarContainer).find('.flatpickr-innerContainer').after(templateCheckTimeElement);

				// fp.config.ignoredFocusElements.push(templateCheckTimeElement.find('input').get(0));

				fp.loadedPlugins.push('confirmDate');
			},
			...(config.showAlways
				? {}
				: {
					/**
					 * @param {any} __
					 * @param {any} dateString
					 */
					onChange(__, dateString) {
						const showCondition = fp.config.mode === 'multiple'
						|| fp.loadedPlugins.includes('monthSelect');
						const localConfirmContainer = fp.calendarContainer.querySelector(`.${ confirmButtonCSSClass}`);
						if (!localConfirmContainer) {
							return;
						}
						if (
							dateString
							&& !fp.config.inline
							&& showCondition
							&& localConfirmContainer
						) {
							localConfirmContainer.classList.add('visible');
						}
						else {
							localConfirmContainer.classList.remove('visible');
						}
					},
				}),
		};
	};
};

export default class KdatepickerController {
	/* @ngInject */
	/**
	 * @param {import('angular').ICompileService} $compile
	 * @param {import('angular').IRootElementService} $element
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 */
	constructor(
		$compile,
		$element,
		$scope,
		$timeout,
		$translate,
	) {
		this.$compile = $compile;
		this.$element = $element;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;

		this.defaultOptions = {
			altFormat:     'd/m/Y',
			altInput:      true,
			displayInput:  true,
			locale:        French,
			/**
			 * @param {Date[]} selectedDates
			 * @param {string} dateString
			 * @param {flatpickr.Instance} instance
			 */
			onClose: (selectedDates, dateString, instance) => {
				if (dateString === '') {
					this.delValue();
				}
				else if (instance.altInput?.value) {
					instance.setDate(instance.altInput.value, true, instance.config.altFormat);
				}
			}
		};

		/** @type {any} */
		this.defaultValue = undefined;

		/** @type {?import('angular').IFormController} */
		this.form = null;

		/** @type {?flatpickr.Instance} */
		this.fpInstance = null;

		/** @type {?string} */
		this.id = null;

		/** @type {any} */
		this.ngDisabled = undefined;

		/** @type {any} */
		this.ngModel = undefined;

		/** @type {?import('angular').INgModelController} */
		this.ngModelCtrl = null;

		/** @type {boolean} */
		this.ngRequired = false;

		/** @type {any} */
		this.options = {};

		/** @type {?Function} */
		this.onSetup = null;

		/** @type {any} */
		this.tempValue = undefined;
	}

	delValue() {
		if (this.fpInstance) {
			this.fpInstance.clear();
		}
		this.setDate(undefined);
	}

	dateValue() {
		// If the date is set, then return the true date value.
		if (this.ngModel) {
			return (this.ngModel instanceof Date)
				? this.ngModel : new Date(this.ngModel);
		}

		return kformUtils.getDateSetting(this.options.defaultDate);
	}

	grabElementAndRunFlatpickr() {
		this.$timeout(() => {
			const element = this.$element.find('.input-kdatepicker').get(0) || this.$element.find('ng-transclude').get(0);
			if (element) {
				this.setDatepicker(element);
			}
		}, 0, true);
	}

	openCalendar() {
		if (this.fpInstance) {
			this.fpInstance.open();
		}
	}

	/**
	 * @param {string} data
	 * @returns {string}
	 */
	parseValue(data) {
		if (this.fpInstance?.altInput) {
			if (data === INFINITEM) {
				this.fpInstance.altInput.value = this.$translate.instant('common.date-infinitem');
			}
			if (data === INFINITEP) {
				this.fpInstance.altInput.value = this.$translate.instant('common.date-infinitep');
			}
		}

		return data;
	}

	reset() {
		if (this.form && this.id && this.id in this.form) {
			this.ngModelCtrl?.$setViewValue(_.cloneDeep(this.defaultValue));
			this.ngModelCtrl?.$validate();
			this.form[this.id].$validate();
			this.ngModelCtrl?.$setPristine();
			this.form[this.id].$setPristine();
		}
	}

	/**
	 * @param {string | number | Date | undefined} date
	 */
	setDate(date) {
		if (date) {
			const oDate = date instanceof Date ? date : new Date(date);
			if (this.options.enableTime) {
				this.ngModelCtrl?.$setViewValue(oDate);
			}
			else {
				this.ngModelCtrl?.$setViewValue(Utils.isoDate(oDate));
			}
		}
		else {
			this.ngModelCtrl?.$setViewValue(undefined);
		}
		this.ngModelCtrl?.$validate();
	}

	/**
	 * @param {HTMLElement} element
	 * @returns {boolean}
	 */
	setDatepicker(element) {
		if (!this.fpInstance) {
			// @ts-ignore
			const FpLibrary = flatpickr || window.FlatpickrInstance;

			if (!FpLibrary) {
				console.warn('Unable to find any flatpickr installation');
				return false;
			}

			this.fpInstance = FpLibrary(element, {
				...this.options,
				onValueUpdate: selectedDates => {
					this.setDate(selectedDates[0]);
				},
			});
		}

		if (typeof this.onSetup === 'function') {
			this.onSetup({
				fpItem: this.fpInstance,
			});
		}

		// If has ngModel set the date
		if (this.ngModel && this.ngModel !== INFINITEM && this.ngModel !== INFINITEP) {
			this.fpInstance?.setDate(this.ngModel);
		}

		// destroy the flatpickr instance when the dom element is removed
		// angular.element(element).on('$destroy', () => {
		// 	this.fpInstance.destroy();
		// });

		// Refresh the scope
		this.$scope.$applyAsync();
		return true;
	}

	setNow() {
		const value = new Date();
		if (!this.options.enableTime) {
			value.setUTCHours(0, 0, 0);
		}
		this.fpInstance?.setDate(value);
		this.setDate(value);

		// sets the form in dirty state (because it doesn't do it!)
		if (this.form) {
			this.form.$setDirty();
		}
	}

	/**
	 * @param {any} value
	 */
	setValue(value) {
		this.ngModelCtrl?.$setViewValue(value);
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		this.grabElementAndRunFlatpickr();

		// Ensure the date value is always a date object when loaded, then unbind the watch.
		if (changes.ngModel) {
			if (this.ngModel === INFINITEM || this.ngModel === INFINITEP) {
				return;
			}

			/** @type {any} */
			const newValue = this.dateValue();
			if (newValue) {
				this.ngModel = newValue;
				this.tempValue = newValue;
			}
			else {
				this.delValue();
			}
		}

		if (
			this.fpInstance?.altInput
			&& changes.ngDisabled
			&& changes.ngDisabled.previousValue !== changes.ngDisabled.currentValue
		) {
			angular.element(this.fpInstance.altInput).attr('disabled', this.ngDisabled);
		}
	}

	$onDestroy() {
		if (this.fpInstance && !Array.isArray(this.fpInstance)) {
			this.fpInstance.destroy();
		}
	}

	$onInit() {
		// Note: I want to keep this.options instance because of 2-way binding
		for (const [k, option] of Object.entries(this.defaultOptions)) {
			if (!(k in this.options)) {
				this.options[k] = option;
			}
		}

		this.$scope.options = this.options;

		if (this.options.closeOnScroll) {
			this.$scope.$on('Form.closeDatepickerPopup', () => {
				if (this.fpInstance) {
					this.fpInstance.close();
				}
			});
		}

		// this.options.plugins = [enableTimePlugin({}, this.$compile, this.$scope)];
		this.grabElementAndRunFlatpickr();

		this.$scope.$watch('options', (/** @type {any} */ newOptions, /** @type {any} */ oldOptions) => {
			// We cannot keep the same instance if enableTime option changes
			if (newOptions.enableTime !== oldOptions.enableTime) {
				if (this.fpInstance) {
					this.fpInstance.destroy();
					this.fpInstance = null;
					this.grabElementAndRunFlatpickr();
				}
				// TODO à vérifier !
				this.setDate(this.ngModel);
			}
			else if (this.fpInstance) {
				for (const [k, option] of Object.entries(newOptions)) {
					if (!_.isEqual(oldOptions[k], option)) {
						// @ts-ignore
						this.fpInstance.set(k, option);
					}
				}
			}
		}, true);

		this.$timeout(() => {
			this.parseValue(this.ngModel);
			this.defaultValue = _.cloneDeep(this.ngModel);
		});

		this.ngModelCtrl?.$parsers.unshift(data => this.parseValue(data));
	}
}
